import { useState, useRef, useCallback } from 'react';
import { Button } from '@/components/ui/button';
import { Download, Plus, Trash2, Pencil, Filter } from 'lucide-react';
import TransactionsSubNav from './TransactionsSubNav';
import TransactionsIncomeView, { TransactionsIncomeViewRef } from './TransactionsIncomeView';
import TransactionsExpensesView, { TransactionsExpensesViewRef } from './TransactionsExpensesView';
import useRolePermissions from '@/hooks/admin/shared/useRolePermissions';
import { formatCurrency } from '@/utils/shared/currencyUtils';
import { cn } from '@/lib/utils';

type SubTab = 'income' | 'expenses';

interface SelectionState {
  count: number;
  total: number;
  onDelete: () => void;
  onEdit?: () => void;
  isDeleting: boolean;
}

const TransactionsTab = () => {
  const [activeSubTab, setActiveSubTab] = useState<SubTab>('income');
  const [incomeSelection, setIncomeSelection] = useState<SelectionState | null>(null);
  const [expensesSelection, setExpensesSelection] = useState<SelectionState | null>(null);
  const [showOnlySelected, setShowOnlySelected] = useState(false);
  
  const incomeViewRef = useRef<TransactionsIncomeViewRef>(null);
  const expensesViewRef = useRef<TransactionsExpensesViewRef>(null);
  const { canCreate, canDelete, canEdit } = useRolePermissions();

  const handleDownloadCSV = () => {
    if (activeSubTab === 'income') {
      incomeViewRef.current?.downloadCSV();
    } else {
      expensesViewRef.current?.downloadCSV();
    }
  };

  const handleCreate = () => {
    if (activeSubTab === 'income') {
      incomeViewRef.current?.createNew();
    } else {
      expensesViewRef.current?.createNew();
    }
  };

  const handleIncomeSelectionChange = useCallback((selection: SelectionState | null) => {
    setIncomeSelection(selection);
    // Reset filter when selection is cleared
    if (!selection) {
      setShowOnlySelected(false);
    }
  }, []);

  const handleExpensesSelectionChange = useCallback((selection: SelectionState | null) => {
    setExpensesSelection(selection);
    // Reset filter when selection is cleared
    if (!selection) {
      setShowOnlySelected(false);
    }
  }, []);

  const currentSelection = activeSubTab === 'income' ? incomeSelection : expensesSelection;
  const hasSelection = currentSelection && currentSelection.count > 0;

  const handleToggleFilter = () => {
    setShowOnlySelected(prev => !prev);
  };

  return (
    <div className="space-y-6">
      <div className="space-y-4">
        <div className="flex items-center justify-between min-h-10">
          <div className="flex items-center gap-3">
            <h2 className="text-2xl font-bold">Transacciones</h2>
            
            {hasSelection && (
              <div className="flex items-center gap-2">
                <div className="bg-blue-50 text-blue-700 px-3 py-1 rounded-full text-sm font-semibold">
                  {currentSelection.count} seleccionado{currentSelection.count > 1 ? 's' : ''} • {formatCurrency(currentSelection.total)}
                </div>
                
                <Button
                  variant="ghost"
                  size="icon"
                  onClick={handleToggleFilter}
                  className={cn(
                    "h-8 w-8",
                    showOnlySelected && "bg-blue-100 text-blue-700 hover:bg-blue-200"
                  )}
                  title={showOnlySelected ? "Mostrar todos" : "Mostrar solo seleccionados"}
                >
                  <Filter size={16} />
                </Button>
              </div>
            )}
          </div>
          
          <div className="flex items-center gap-2">
            {hasSelection && canDelete && (
              <Button
                variant="destructive"
                onClick={currentSelection.onDelete}
                className="flex items-center gap-2"
                disabled={currentSelection.isDeleting}
              >
                <Trash2 size={16} />
                Eliminar
              </Button>
            )}

            {hasSelection && canEdit && currentSelection.onEdit && (
              <Button
                variant="outline"
                onClick={currentSelection.onEdit}
                className="flex items-center gap-2"
              >
                <Pencil size={16} />
                Editar
              </Button>
            )}

            <Button
              onClick={handleDownloadCSV}
              variant="outline"
              className="flex items-center gap-2"
            >
              <Download size={16} />
              Descargar CSV
            </Button>

            {canCreate && (
              <Button
                onClick={handleCreate}
                className="bg-brand-primary hover:bg-brand-primary-hover flex items-center gap-2"
              >
                <Plus size={16} />
                {activeSubTab === 'income' ? 'Crear Ingreso' : 'Crear Gasto'}
              </Button>
            )}
          </div>
        </div>
        
        <TransactionsSubNav 
          activeSubTab={activeSubTab} 
          onSubTabChange={setActiveSubTab} 
        />
      </div>

      {activeSubTab === 'income' ? (
        <TransactionsIncomeView 
          ref={incomeViewRef} 
          onSelectionChange={handleIncomeSelectionChange}
          showOnlySelected={showOnlySelected}
        />
      ) : (
        <TransactionsExpensesView 
          ref={expensesViewRef}
          onSelectionChange={handleExpensesSelectionChange}
          showOnlySelected={showOnlySelected}
        />
      )}
    </div>
  );
};

export default TransactionsTab;